/**
 * Tipos para o sistema de roteamento com múltiplas ações
 * 
 * Suporta sequência de ações: dados, envio, delay
 * Arquitetura similar ao CampaignMessageBuilder
 * 
 * @version 2.0.0
 */

import { RoutingActionType } from '@/pages/tenant/RoutingRules';

// Interface para item de ação de roteamento
export interface RoutingActionItem {
  id: string;
  action_type: RoutingActionType;
  action_value: Record<string, unknown>;
  sort_order: number;
  delay_seconds: number;
}

// Interface para registro no banco de dados
export interface RoutingRuleActionRecord {
  id?: string;
  rule_id: string;
  action_type: RoutingActionType;
  action_value: Record<string, unknown>;
  sort_order: number;
  delay_seconds: number;
  created_at?: string;
}

// Grupos de tipos de ação
export const dataActionTypes: RoutingActionType[] = [
  'assign_department',
  'assign_agent',
  'add_tag',
  'remove_tag',  // v2.1: Remover tag de contato
  'set_priority',
  'assign_phase',
  'assign_global_queue',  // v2.2: Distribuir via fila global
];

export const sendActionTypes: RoutingActionType[] = [
  'send_text',
  'send_image',
  'send_video',
  'send_audio',
  'send_document',
  'send_link',
  'send_button_link',  // v2.3: Botão com link clicável
  'send_template',     // v2.4: Envio de template WhatsApp (WABA)
];

// Configuração visual dos tipos de ação (design profissional sem emojis)
export interface ActionTypeConfig {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  group: 'data' | 'send' | 'control';
}

export const actionTypeConfig: Record<RoutingActionType | 'delay', ActionTypeConfig> = {
  // Ações de dados
  assign_department: { 
    label: 'Departamento', 
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    group: 'data'
  },
  assign_agent: { 
    label: 'Agente', 
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    group: 'data'
  },
  add_tag: { 
    label: 'Adicionar Tag', 
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    group: 'data'
  },
  // v2.1: Remover tag de contato
  remove_tag: { 
    label: 'Remover Tag', 
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    group: 'data'
  },
  set_priority: { 
    label: 'Prioridade', 
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    group: 'data'
  },
  assign_phase: { 
    label: 'Fase Kanban', 
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    group: 'data'
  },
  // v2.2: Distribuir via fila global
  assign_global_queue: { 
    label: 'Fila Global', 
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    group: 'data'
  },
  
  // Ações de envio
  send_text: { 
    label: 'Texto', 
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-200',
    group: 'send'
  },
  send_image: { 
    label: 'Imagem', 
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    group: 'send'
  },
  send_video: { 
    label: 'Vídeo', 
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    group: 'send'
  },
  send_audio: { 
    label: 'Áudio', 
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    group: 'send'
  },
  send_document: { 
    label: 'Documento', 
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    group: 'send'
  },
  send_link: { 
    label: 'Link', 
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    group: 'send'
  },
  // v2.3: Botão com link clicável (CTA)
  send_button_link: { 
    label: 'Botão Link', 
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    group: 'send'
  },
  // v2.4: Envio de template WhatsApp (WABA)
  send_template: { 
    label: 'Template', 
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    group: 'send'
  },
  
  // Ações de controle
  delay: { 
    label: 'Delay', 
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    group: 'control'
  },
};

// Tipos de mídia para galeria
export type RoutingMediaType = 'image' | 'video' | 'audio' | 'document';

// Mapeamento de RoutingActionType para tipo de mídia
export const actionToMediaType: Partial<Record<RoutingActionType, RoutingMediaType>> = {
  send_image: 'image',
  send_video: 'video',
  send_audio: 'audio',
  send_document: 'document',
};

// ============= INTERFACES PARA WEBHOOK =============

/**
 * Interface para log de webhook de roteamento
 * Corresponde à tabela routing_webhook_logs
 */
export interface RoutingWebhookLog {
  id: string;
  rule_id: string;
  tenant_id: string;
  request_payload: Record<string, unknown>;
  request_ip: string;
  contact_phone: string | null;
  contact_id: string | null;
  conversation_id: string | null;
  status: 'success' | 'error' | 'validation_error';
  error_message: string | null;
  error_code: string | null;
  processing_time_ms: number;
  actions_executed: number;
  actions_failed: number;
  created_at: string;
  total_count?: number; // Para paginação server-side
}

/**
 * Interface para estatísticas de webhook
 */
export interface RoutingWebhookStats {
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  successRate: number;
  lastRequestAt: string | null;
  avgProcessingTime: number;
  totalActionsExecuted: number;
}

/**
 * Interface para dados de volume por hora
 */
export interface RoutingWebhookVolumeData {
  hora: number;
  data_hora: string;
  total: number;
  sucesso: number;
  erro: number;
}

/**
 * Interface para resultado paginado de logs
 */
export interface PaginatedRoutingLogsResult {
  logs: RoutingWebhookLog[];
  totalCount: number;
}

/**
 * Interface para regra de roteamento estendida (inclui dados de webhook)
 */
export interface RoutingRuleWithWebhook {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  priority: number;
  condition_type: string;
  condition_value: Record<string, unknown>;
  action_type: string;
  action_value: Record<string, unknown>;
  inbox_ids: string[] | null;
  stop_on_match: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Campos específicos de webhook
  webhook_token: string | null;
  webhook_total_requests: number;
  webhook_successful_requests: number;
  webhook_failed_requests: number;
  webhook_last_request_at: string | null;
  field_mapping: Record<string, string> | null;
}
